import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Loader2, CheckCircle2, User, Calendar, Stethoscope, Phone, Mail, AlertTriangle, HeartPulse, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import siteConfig from '../config/siteConfig';

const TIME_PREFERENCES = [
  'Morning (8am - 12pm)',
  'Afternoon (12pm - 4pm)',
  'Evening (4pm - 7pm)',
  'Any Time Available'
];

export default function PatientRequestPage() {
  const { company, contact, logo, patientRequest, visitTypes, medicalConditions } = siteConfig;
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    preferred_date: '',
    preferred_time: '',
    visit_type: '',
    chief_complaint: '',
    symptoms_duration: '',
    current_medications: '',
    allergies: '',
    medical_conditions: '',
    additional_notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCondition = (condition) => {
    setSelectedConditions(prev => {
      const updated = prev.includes(condition)
        ? prev.filter(c => c !== condition)
        : [...prev, condition];
      // Also update formData
      setFormData(f => ({ ...f, medical_conditions: updated.join(', ') }));
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const required = ['first_name', 'last_name', 'email', 'phone', 'date_of_birth', 'gender', 'visit_type', 'chief_complaint'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/patient-requests', formData);
      setReferenceNumber(response.data.reference_number);
      setSubmitted(true);
      toast.success('Your request has been submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSubmitted(false);
    setSelectedConditions([]);
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', gender: '',
      preferred_date: '', preferred_time: '', visit_type: '', chief_complaint: '',
      symptoms_duration: '', current_medications: '', allergies: '',
      medical_conditions: '', additional_notes: '',
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-lg text-center border-0 shadow-xl">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-900">{patientRequest.successTitle}</h2>
            <p className="text-slate-600 mb-6">{patientRequest.successMessage}</p>
            <div className="bg-slate-100 p-4 rounded-xl mb-6">
              <p className="text-sm text-slate-500 mb-1">Your Reference Number</p>
              <p className="text-2xl font-mono font-bold text-slate-900">{referenceNumber}</p>
            </div>
            <Alert className="text-left mb-6 bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Important:</strong> {siteConfig.email.emergencyDisclaimer}
              </AlertDescription>
            </Alert>
            <p className="text-sm text-slate-500 mb-6">
              A confirmation email has been sent to your email address.
            </p>
            <div className="space-y-2">
              <Button onClick={() => window.location.href = '/'} className="w-full bg-slate-900 hover:bg-slate-800">
                Back to Home
              </Button>
              <Button variant="outline" onClick={resetForm} className="w-full">
                Submit Another Request
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="patient-request-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-slate-500 hover:text-slate-900 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <img 
            src={logo.url} 
            alt={logo.alt} 
            className="w-20 h-20 mx-auto mb-4 rounded-xl object-contain shadow-lg"
          />
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{patientRequest.title}</h1>
          <p className="text-slate-600 mt-2">{patientRequest.subtitle}</p>
        </div>

        {/* Emergency Notice */}
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>Medical Emergency?</strong> {patientRequest.emergencyNotice}
          </AlertDescription>
        </Alert>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {patientRequest.features.map((feature, i) => {
            const icons = [Stethoscope, Calendar, ShieldCheck];
            const Icon = icons[i] || Stethoscope;
            return (
              <Card key={i} className="text-center p-4 border-0 shadow-md bg-white/80 backdrop-blur">
                <Icon className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                <h3 className="font-semibold text-sm text-slate-900">{feature.title}</h3>
                <p className="text-xs text-slate-500">{feature.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Request Form */}
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-slate-900">{patientRequest.formTitle}</CardTitle>
            <CardDescription>{patientRequest.formSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <User className="w-4 h-4" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input id="first_name" value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} placeholder="John" required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input id="last_name" value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} placeholder="Doe" required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="john.doe@email.com" required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder={contact.phone} required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input id="date_of_birth" type="date" value={formData.date_of_birth} onChange={(e) => handleChange('date_of_birth', e.target.value)} required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Appointment Preferences */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <Calendar className="w-4 h-4" />
                  Appointment Preferences
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="visit_type">Type of Visit *</Label>
                    <Select value={formData.visit_type} onValueChange={(v) => handleChange('visit_type', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select visit type" /></SelectTrigger>
                      <SelectContent>
                        {visitTypes.map(type => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferred_date">Preferred Date</Label>
                    <Input id="preferred_date" type="date" value={formData.preferred_date} onChange={(e) => handleChange('preferred_date', e.target.value)} min={new Date().toISOString().split('T')[0]} className="bg-white" />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="preferred_time">Preferred Time</Label>
                    <Select value={formData.preferred_time} onValueChange={(v) => handleChange('preferred_time', v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Select preferred time" /></SelectTrigger>
                      <SelectContent>
                        {TIME_PREFERENCES.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Medical Information */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <Stethoscope className="w-4 h-4" />
                  Medical Information
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="chief_complaint">Chief Complaint / Reason for Visit *</Label>
                    <Textarea id="chief_complaint" value={formData.chief_complaint} onChange={(e) => handleChange('chief_complaint', e.target.value)} placeholder="Please describe your main concern or reason for requesting this appointment..." rows={3} required className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="symptoms_duration">How long have you had these symptoms?</Label>
                    <Input id="symptoms_duration" value={formData.symptoms_duration} onChange={(e) => handleChange('symptoms_duration', e.target.value)} placeholder="e.g., 3 days, 2 weeks, 1 month" className="bg-white" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="current_medications">Current Medications</Label>
                      <Textarea id="current_medications" value={formData.current_medications} onChange={(e) => handleChange('current_medications', e.target.value)} placeholder="List any medications you're currently taking..." rows={2} className="bg-white" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allergies">Known Allergies</Label>
                      <Textarea id="allergies" value={formData.allergies} onChange={(e) => handleChange('allergies', e.target.value)} placeholder="List any known allergies..." rows={2} className="bg-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing Medical Conditions */}
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-900">
                  <HeartPulse className="w-4 h-4" />
                  Existing Medical Conditions
                </h3>
                <p className="text-sm text-slate-500 mb-4">Select any conditions you currently have or have been diagnosed with:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {medicalConditions.map((condition) => (
                    <div key={condition} className="flex items-center space-x-2">
                      <Checkbox
                        id={`condition-${condition}`}
                        checked={selectedConditions.includes(condition)}
                        onCheckedChange={() => toggleCondition(condition)}
                      />
                      <Label htmlFor={`condition-${condition}`} className="text-sm font-normal cursor-pointer">
                        {condition}
                      </Label>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2">
                  <Label htmlFor="other_conditions">Other conditions not listed above:</Label>
                  <Input 
                    id="other_conditions" 
                    placeholder="List any other medical conditions..."
                    onChange={(e) => {
                      const otherConditions = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        medical_conditions: selectedConditions.length > 0 
                          ? `${selectedConditions.join(', ')}${otherConditions ? ', ' + otherConditions : ''}`
                          : otherConditions
                      }));
                    }}
                    className="bg-white"
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="additional_notes">Additional Notes</Label>
                <Textarea id="additional_notes" value={formData.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} placeholder="Any other information you'd like us to know..." rows={3} className="bg-white" />
              </div>

              <Alert className="bg-slate-50 border-slate-200">
                <AlertDescription className="text-slate-600">{patientRequest.consentText}</AlertDescription>
              </Alert>

              <Button type="submit" size="lg" className="w-full bg-slate-900 hover:bg-slate-800 font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {patientRequest.submitButton}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="mt-6 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 text-slate-900">{patientRequest.needHelp}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-slate-600">
                <Phone className="w-4 h-4 mt-0.5" />
                <div className="flex flex-col">
                  {(contact.phones || [contact.phone]).map((num, i) => (
                    <a key={i} href={`tel:${num.replace(/[^+\d]/g, '')}`} className="hover:text-emerald-600 transition-colors">{num}</a>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                <span>{contact.email}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
